import React, { useState } from 'react';

const CreateMemorial: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  
  const faqs = [
    {
      question: "How long does it take to create a Digital Memorial?",
      answer: "The basic memorial can be set up within a couple of days. However, we recommend taking your time to gather photos and other content you wish to be displayed. Our team will guide you through each step, ensuring everything is perfect before publishing."
    },
    {
      question: "Can family members contribute to the memorial?",
      answer: "Yes, absolutely! Family members and friends can contribute photos, stories, and messages to the memorial. You can control who has permission to add content, and all contributions can be moderated before being published."
    },
    {
      question: "How secure is a Digital Memorial?",
      answer: "We take security very seriously. Your memorial is protected with enterprise-grade encryption, and you have complete control over privacy settings. You can make the memorial public, private, or share it with specific people through secure invitation links."
    },
    {
      question: "What ongoing support do you provide?",
      answer: "Your Digital Memorial is built to last, using enterprise-grade infrastructure that ensures an isolated environment for each one. For those planning a longer-term Digital Memorial, we offer optional maintenance packages that include regular updates, content backups, technical support, and design refreshes to keep your memorial current with modern web standards. Our team remains available to assist with any questions or changes you need, whether you opt for a maintenance package or not."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Choose Your Design",
      description: "Select from our thoughtfully crafted memorial styles, each designed to create a unique and meaningful tribute to your loved one."
    },
    {
      number: "02",
      title: "Build Their Story",
      description: "Collect photos, write tributes, create timelines, and gather memories that capture the essence of your loved one's life and legacy."
    },
    {
      number: "03",
      title: "Share & Grow Together",
      description: "Invite family and friends to contribute their memories, making it a living memorial that grows more meaningful over time."
    }
  ];

  const features = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      title: "Digital Obituary",
      description: "More than just dates and facts – create a living story of their journey. Share their achievements, passions, and the little moments that made them special. Perfect for preserving their unique personality and helping future generations understand their legacy."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: "Photo Gallery",
      description: "Create beautiful collections of cherished photographs that tell their story visually. From milestone moments to candid snapshots, each image helps paint a picture of their life. Ideal for sharing those precious memories that words alone can't capture."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Life Timeline",
      description: "Map out the beautiful journey of their life through an interactive timeline. Highlight key moments, achievements, and memorable stories in chronological order. Essential for preserving their history and helping others explore their life's journey at their own pace."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      title: "Book of Condolences",
      description: "A thoughtfully designed space where family and friends can share their messages of support. These heartfelt tributes become a source of comfort and strength, creating a lasting collection of love and remembrance that you can revisit whenever you need, from wherever you are."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      title: "Memory Wall",
      description: "An interactive space where everyone can contribute their unique memories and perspectives. Family and friends can share stories, photos, videos, and moments you might never have known about. Perfect for discovering new dimensions of your loved one's life through others' experiences."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1H3a2 2 0 110-4h1a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1V3" />
        </svg>
      ),
      title: "Custom Features",
      description: "Every life is unique, and your memorial can be too. Whether it's adding special music that meant something to them, sharing video memories, or creating custom sections that capture their specific interests – we can build any feature you envision to honor their memory."
    }
  ];

  return (
    <div className="relative">
      {/* Hero Section with Process Breakdown */}
      <div className="pt-48 md:pt-48">
        <div className="max-w-[1400px] mx-auto px-6">
          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-center mb-16 lg:mb-24">
            <span className="text-white">We Take Care of Everything,</span>
            <div className="mt-4">
              <span className="text-white">From Start </span>
              <span className="bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text">To Finish</span>
            </div>
          </h1>

          {/* Desktop Layout */}
          <div className="hidden lg:flex">
            {/* Left Side - Steps */}
            <div className="w-1/2 pr-20 space-y-16">
              {steps.map((step, index) => (
                <div key={index} className="flex items-start gap-6">
                  {/* Step Number */}
                  <div className="flex-none">
                    <span className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text font-montserrat">
                      {step.number}
                    </span>
                  </div>

                  {/* Step Content */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-3 font-montserrat tracking-tight">
                      {step.title}
                    </h3>
                    <p className="text-[#FFFFFFCC] text-base leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Side - Image */}
            <div className="w-1/2 relative group">
              {/* Animated Background Elements */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-primary/[0.35] rounded-full blur-[100px] animate-pulse" 
                     style={{ animationDuration: '10s' }} />
                <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-primary/[0.35] rounded-full blur-[100px] animate-pulse"
                     style={{ animationDuration: '15s' }} />
              </div>

              <div className="relative transform group-hover:scale-[1.02] transition-transform duration-700">
                <img
                  src="/images/Create-hero-mac.svg"
                  alt="Memorial Creation Preview"
                  className="w-[150%] h-auto object-contain translate-x-[25%] translate-y-[15%] 2xl:scale-[1.7] xl:scale-[1.5] lg:scale-[1.3]"
                />
              </div>
            </div>
          </div>

          {/* Mobile/Tablet Layout */}
          <div className="lg:hidden">
            <div className="max-w-2xl mx-auto space-y-16">
              {steps.map((step, index) => (
                <div key={index} className="text-center">
                  {/* Step Number */}
                  <span className="text-5xl font-bold bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text font-montserrat mb-6 block">
                    {step.number}
                  </span>

                  {/* Step Content */}
                  <h3 className="text-2xl font-bold text-white mb-4 font-montserrat tracking-tight">
                    {step.title}
                  </h3>
                  <p className="text-[#FFFFFFCC] text-lg leading-relaxed max-w-xl mx-auto">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-20 md:py-32 md:py-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-24 mt-12 md:mt-0">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              <span className="text-white">Digital Memorial</span>
              <span className="bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text"> Features</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-24">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group relative"
              >
                {/* Feature Icon */}
                <div className="relative z-10 mb-8">
                  <div className="w-16 h-16 relative">
                    {/* Icon Glow */}
                    <div className="absolute inset-0 bg-primary/10 rounded-full blur-lg transform scale-150 opacity-0 group-hover:opacity-100 transition-all duration-700" />
                    
                    {/* Icon Container */}
                    <div className="relative h-full w-full flex items-center justify-center">
                      <div className="text-primary transition-transform duration-500 transform group-hover:scale-110">
                        {feature.icon}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature Content */}
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-white font-montserrat tracking-tight mb-4 group-hover:text-primary transition-all duration-500">
                    {feature.title}
                  </h3>
                  
                  {/* Subtle Divider */}
                  <div className="w-8 h-[1px] bg-gradient-to-r from-primary to-transparent mb-4 opacity-50 group-hover:w-12 transition-all duration-500" />
                  
                  <p className="text-[#FFFFFFCC] text-base leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Ambient Background Elements */}
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-primary/[0.03] rounded-full blur-3xl -z-10 animate-pulse" style={{ animationDuration: '10s' }} />
          <div className="absolute bottom-1/3 left-1/4 w-[500px] h-[500px] bg-primary/[0.03] rounded-full blur-3xl -z-10 animate-pulse" style={{ animationDuration: '15s' }} />
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          {/* Main Title */}
          <div className="mb-24">
            <h2 className="text-5xl md:text-6xl font-bold tracking-tight">
              <span className="text-white">The Lasting Impact of</span>
              <div className="mt-4">
                <span className="bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text">Digital Memorials</span>
              </div>
            </h2>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-24 gap-y-20">
            {/* Benefit 1 */}
            <div className="relative">
              <div className="h-12 w-1 bg-gradient-to-b from-primary to-transparent absolute -left-6" />
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-montserrat">
                Eternal Accessibility
              </h3>
              <p className="text-[#FFFFFFCC] text-lg leading-relaxed">
                Unlike traditional memorials, Digital Memorials can be visited and cherished from anywhere in the world, allowing family and friends to stay connected with memories forever
              </p>
            </div>

            {/* Benefit 2 */}
            <div className="relative">
              <div className="h-12 w-1 bg-gradient-to-b from-primary to-transparent absolute -left-6" />
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-montserrat">
                Living Memorial
              </h3>
              <p className="text-[#FFFFFFCC] text-lg leading-relaxed">
                A dynamic space that grows over time as family and friends contribute their own memories, photos, and stories, creating a rich tapestry of your loved one's life
              </p>
            </div>

            {/* Benefit 3 */}
            <div className="relative">
              <div className="h-12 w-1 bg-gradient-to-b from-primary to-transparent absolute -left-6" />
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-montserrat">
                Multi-Generational Legacy
              </h3>
              <p className="text-[#FFFFFFCC] text-lg leading-relaxed">
                Preserve stories and memories for future generations, ensuring your loved one's legacy lives on and can be discovered by children, grandchildren, and beyond
              </p>
            </div>

            {/* Benefit 4 */}
            <div className="relative">
              <div className="h-12 w-1 bg-gradient-to-b from-primary to-transparent absolute -left-6" />
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-montserrat">
                Private & Secure
              </h3>
              <p className="text-[#FFFFFFCC] text-lg leading-relaxed">
                Unlike memorial profile platforms, your Digital Memorial remains completely private and secure. You control who can view and contribute, ensuring your cherished memories are shared only with those you trust
              </p>
            </div>
          </div>

          {/* Ambient Background Elements */}
          <div className="absolute top-1/2 right-1/4 w-[600px] h-[600px] bg-primary/[0.02] rounded-full blur-3xl -z-10 animate-pulse" style={{ animationDuration: '12s' }} />
          <div className="absolute bottom-1/2 left-1/3 w-[600px] h-[600px] bg-primary/[0.02] rounded-full blur-3xl -z-10 animate-pulse" style={{ animationDuration: '18s' }} />
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              <span className="text-white">Frequently Asked</span>
              <span className="bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text"> Questions</span>
            </h2>
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-[#1A1A1A]/50 border border-white/10 rounded-2xl overflow-hidden hover:border-primary/30 transition-colors duration-300">
                <button
                  className="w-full px-8 py-6 text-left flex items-center justify-between"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <h3 className="text-xl font-montserrat font-semibold text-white">
                    {faq.question}
                  </h3>
                  <svg
                    className={`w-6 h-6 text-primary transform transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === index && (
                  <div className="px-8 pb-6">
                    <p className="text-[#FFFFFFCC] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Ambient Background Elements */}
          <div className="absolute top-1/2 right-1/3 w-[600px] h-[600px] bg-primary/[0.02] rounded-full blur-3xl -z-10 animate-pulse" style={{ animationDuration: '15s' }} />
        </div>
      </div>
    </div>
  );
};

export default CreateMemorial; 